import { inject, Injectable } from '@angular/core';
import { CmsApiService } from '../../core/services/api/cms-api.service';
import { CmsContentMapperService } from '../../core/services/cms-content-mapper.service';
import { SsApiService } from '../../core/services/api/ss-api.service';
import { filter, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { TimelineStatus, TimeService } from '../../core/services/time.service';
import { ITournament } from './tournaments.interface';
import { BreakpointsDetectorService } from '../../core/services/breakpoints-detector.service';
import { LanguageService } from '../../core/services/language.service';
import { ResponseHandlerService } from 'src/app/core/services/response-handler.service';
import { GameListConfig } from 'src/app/core/shared/components/game-list/game-list.component';
import { isArray, isNullOrUndefined } from '../../core/helpers/utils';
import { CurrencyService } from '../../core/services/currency.service';
import { ContentUpdaterService } from '../../core/services/content-updater/content-updater.service';

enum GamesCount {
  MOBILE = 5,
  DESKTOP = 9,
}

@Injectable({
  providedIn: 'root',
})
export class TournamentsService {
  private _api = inject(CmsApiService);
  private _mapper = inject(CmsContentMapperService);
  private _ssApi = inject(SsApiService);
  private _time = inject(TimeService);
  private _lang = inject(LanguageService);
  private _breakPoint = inject(BreakpointsDetectorService);
  private _responseHandler = inject(ResponseHandlerService);
  private _currency = inject(CurrencyService);
  private _contentUpdater = inject(ContentUpdaterService);

  /**
   * Current tournament
   */
  public tournament$: BehaviorSubject<ITournament> = new BehaviorSubject<ITournament>(null);

  /**
   * Config for tournament games
   */
  public tournamentGamesConfig: GameListConfig;

  /**
   * Get promotions list from CMS and prepare for using in frontend
   * @param params
   */
  list(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('tournament/list', params, () =>
      this._api.tournamentList(params).pipe(
        map(response => this._mapper.mapCmsData(response.data.list, {
          id: 'id',
          slug: 'slug',
          startAt: 'startAt.date',
          endAt: 'endAt.date',
          publishAt: 'publishAt',
          unpublishAt: 'unpublishAt',
          gameList: 'gameList',
          isMultiTournament: 'isMultiTournament',
          identifier: 'identifier',
        })),
        map((list: ITournament[]) => list.filter(item => !isNullOrUndefined(item.gameList) && item?.gameList?.length)),
        map((list: ITournament[]) => this._mapTournamentTopPlayers(list)),
        switchMap(list => this._resolveMultiTournamentList(list)),
        map(list => list.sort((a, b) => a.endAt - b.endAt)),
        map(list => {
          return {
            now: list.filter(item => item.tournamentTimeStatus === TimelineStatus.NOW),
            future: list.filter(item => item.tournamentTimeStatus === TimelineStatus.FUTURE),
            past: list.filter(item => item.tournamentTimeStatus === TimelineStatus.PAST),
          };
        }),
      ));
  }

  /**
   * Get promotions item from CMS and prepare for using in frontend
   * @param params
   */
  item(params: object = {}): Observable<any> {
    return this._contentUpdater.registerContent('tournament/item', params, () =>
      this._api.tournamentItem(params).pipe(
        tap(res => this._responseHandler.handleResponseStatus(res?.statusCode, '/tournaments')),
        switchMap(response => {
          if (response.data) {
            return of(this._mapTournamentList(
              this._mapper.mapCmsData(response.data ? [response.data] : [], {
                id: 'id',
                slug: 'slug',
                startAt: 'startAt.date',
                endAt: 'endAt.date',
                identifier: 'identifier',
                gameList: 'gameList',
                isMultiTournament: 'isMultiTournament',
              })));
          }
          return EMPTY;
        }),
        switchMap((list: any[]) => {
          return list[0]?.isMultiTournament ? this._resolveMultiTournamentItem(list[0]) : of(list);
        }),
        map((list: ITournament[]) => this._mapTournamentTopPlayers(list)),
      ),
    );
  }

  public findTournamentsByGameId(gameId: number, currentTournaments) {
    const matchingTournaments = [];

    currentTournaments.forEach(
      tournament => {
        const hasGame = tournament.gameList
          .some(game => game.id === gameId);

        if (hasGame) {
          matchingTournaments.push(tournament);
        }
      });

    return matchingTournaments;
  }


  /**
   * Map additional data for tournaments
   *
   * @param list
   * @private
   */
  private _mapTournamentList(list: Array<any>): Array<any> {
    if (!isArray(list)) {
      return null;
    }

    return list.map(tournament => {

      const startAt = this._time.toLocalDate(tournament.startAt);
      const endAt = this._time.toLocalDate(tournament.endAt);

      return {
        ...tournament,
        startAt: new Date(startAt),
        endAt: new Date(endAt),
        prizesData$: tournament.isMultiTournament ? of(this._mapSsTournament(tournament)) : this._resolvePrizesData$(tournament),
        tournamentTimeStatus: this._time.resolveTimeStatus(startAt, endAt),
        tournamentUserStatus$: this._resolveUserStatus$(tournament),
      };
    });
  }

  /**
   * Map top players stream
   * @param list
   * @private
   */
  private _mapTournamentTopPlayers(list: ITournament[]) {
    return list.map(item => {
      return {
        ...item,
        topPlayers$: this._resolveTopPlayers$(item),
      };
    });
  }

  /**
   * Map additional data for multi tournaments list
   * @returns
   * @param tournamentList
   */
  private _resolveMultiTournamentList(tournamentList: any[]): Observable<any> {
    return this._ssApi.tournamentsAll().pipe(
      map((tournamentSSList: any[]) => {
        return tournamentList.map(tournament => {
          tournament.activeMultiTournament = tournamentSSList.filter(e => e.in_progress).find(e => Number(e.frontend_identifier) === tournament.id);
          if (tournament.isMultiTournament && tournament.activeMultiTournament) {
            this._resolveTimeForMultiTournament(tournament);
            return tournament;
          } else {
            return this._mapTournamentList([tournament])[0];
          }
        });
      }),
    );
  }

  /**
   * Map additional data for multi tournaments
   * @param tournament
   * @returns
   */
  private _resolveMultiTournamentItem(tournament): Observable<any> {
    return this._ssApi.tournamentsAll().pipe(
      map((list: any[]) => {
        tournament.activeMultiTournament = list
          .filter(e => e.in_progress)
          .find(e => Number(e.frontend_identifier) === tournament.id);
        this._resolveTimeForMultiTournament(tournament);
        tournament.activeMultiTournament.isMultiTournament = true;
        tournament.activeMultiTournament = this._mapTournamentList([tournament.activeMultiTournament])[0];
        tournament.activeMultiTournament = this._mapTournamentTopPlayers([tournament.activeMultiTournament])[0];
        return [tournament];
      }),
    );
  }

  private _resolveTimeForMultiTournament(tournament) {
    tournament.startAt = this._time.toLocalDate(tournament.activeMultiTournament ? tournament.activeMultiTournament.start_at : tournament.startAt);
    tournament.endAt = this._time.toLocalDate(tournament.activeMultiTournament ? tournament.activeMultiTournament.end_at : tournament.endAt);
    tournament.tournamentTimeStatus = this._time.resolveTimeStatus(tournament.startAt, tournament.endAt);
  }

  /**
   * Resolve prizes data stream
   * @param tournament
   * @private
   */
  private _resolvePrizesData$(tournament) {
    return tournament.identifier ? this._ssApi.tournaments(tournament.identifier).pipe(
      map(response => this._mapSsTournament(response)), share()) : of(null);
  }

  /**
   * Resolve user status stream
   * @param tournament
   * @private
   */
  private _resolveUserStatus$(tournament) {
    if (tournament.isMultiTournament) {
      return this._ssApi.tournamentsStatus(tournament.id).pipe(shareReplay());
    } else {
      return tournament.identifier ? this._ssApi.tournamentsStatus(tournament.identifier)
        .pipe(shareReplay()) : of(null);
    }
  }

  /**
   * Resolve top players stream
   * @param tournament
   * @private
   */
  private _resolveTopPlayers$(tournament: ITournament) {
    return combineLatest([tournament?.prizesData$, tournament?.tournamentUserStatus$]).pipe(
      map(([data, status]) => this.mapTopPlayers(data, status)?.length ?
        this.mapTopPlayers(data, status) : of([])), share());
  }


  /**
   * Prepare tournament response from SS for using in frontend
   *
   * @param tournament
   * @private
   */
  private _mapSsTournament(tournament: any): any {
    return {
      ...tournament,
      currency_symbol: this._currency.currencySymbol(tournament.currency),
      mappedPrizes: this._mapPrizesList(tournament.prizes || []),
    };
  }

  /**
   * Combine places with same prizes
   *
   * @param list
   * @private
   */
  private _mapPrizesList(list: any[]): any[] {
    const result = [];

    for (let i = 0; i < list.length; i++) {
      const wantedKey = (list[i].freespins_count || 0).toString() + list[i].money_award;
      const wantedIndex = list.lastIndexOf(list.slice().reverse().find(item => {

        return wantedKey === ((item.freespins_count || 0).toString() + item.money_award);
      }));

      const prize = {
        ...list[i],
        place: wantedIndex !== i ? list[i].award_place + '-' + list[wantedIndex].award_place : list[i].award_place,
        place_suffix: wantedIndex !== i ? '' : ['st', 'nd', 'rd'][((list[i].award_place + 90) % 100 - 10) % 10 - 1] || 'th',
        multiple: wantedIndex !== i,
      };

      result.push(prize);

      if (wantedIndex > i) {
        i = wantedIndex;
      }
    }

    return result;
  }

  /**
   * Mapped winners and set current user position
   * @param data
   * @param status
   * @private
   */
  mapTopPlayers(data: any, status: any) {

    const topPlayers = data?.top_players;

    if (!status?.award_place) {
      return topPlayers;
    }

    let players;

    if (!topPlayers.some(player => player.award_place === status.award_place)) {
      status.current_user = true;
      players = [...topPlayers, status];
    } else {
      topPlayers.forEach((player => {
        if (player.award_place === status.award_place) {
          player.current_user = true;
        }
      }));
      players = [...topPlayers];
    }
    return players.sort((a, b) => a.award_place - b.award_place);
  }


  public getTournamentBySlug$(slug: string): Observable<any> {
    return this.item({ slug }).pipe(
      filter(data => !!data),
      map(data => data[0]),
      tap((tournament: ITournament) => {
        this.tournament$.next(tournament);
        this.tournamentGamesConfig = {
          link: `${this._lang.currentRouteLanguage}/tournaments/${tournament.slug}/games`,
          type: 'tournament',
          filter: { limit: this._setGamesLimit(tournament) },
          title: 'link.games',
          gameList: tournament?.gameList,
        };
      }),
    );
  }

  /**
   * Set games limit
   * @param tournament
   * @private
   */
  private _setGamesLimit(tournament: any) {
    switch (true) {
      case tournament?.gameList.length < GamesCount.DESKTOP && this._breakPoint.isDesktop:
        return tournament?.gameList.length;
      case tournament?.gameList.length >= GamesCount.DESKTOP && this._breakPoint.isDesktop:
        return GamesCount.DESKTOP;
      case tournament?.gameList.length < GamesCount.MOBILE && this._breakPoint.isMobile:
        return tournament?.gameList.length;
      case tournament?.gameList.length >= GamesCount.MOBILE && this._breakPoint.isMobile:
        return GamesCount.MOBILE;
    }
  }
}
